import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroupDirective,
  NgForm,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ThemeService } from '@ld.consumer-portal/utils';

export interface NativeAuthSendCodeEvent {
  email: string;
  firstName: string;
  lastName: string;
}

export interface NativeAuthVerifyCodeEvent {
  otp: string;
}

export interface NativeAuthSetPasswordEvent {
  password: string;
}

export interface NativeAuthSigninMfaVerifyEvent {
  otp: string;
}

export type NativeAuthStep = 'send-code' | 'verify-code' | 'set-password' | 'account-created' | 'signin-mfa-verify';

export class ConfirmPasswordErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(
      control &&
      (control.invalid || form?.hasError('passwordMismatch')) &&
      (control.dirty || control.touched)
    );
  }
}

@Component({
  selector: 'ld-register-card',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatIconModule, MatInputModule, TranslocoDirective],
  templateUrl: './register-card.component.html',
  styleUrl: './register-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterCardComponent {
  private fb = inject(FormBuilder);
  private readonly transloco = inject(TranslocoService);
  private themeService = inject(ThemeService);
  private readonly injector = inject(Injector);

  private readonly firstNameInputRef = viewChild<ElementRef<HTMLInputElement>>('firstNameInput');
  private readonly registerEmailInputRef = viewChild<ElementRef<HTMLInputElement>>('registerEmailInput');
  private readonly otpInputRef = viewChild<ElementRef<HTMLInputElement>>('otpInput');
  private readonly passwordInputRef = viewChild<ElementRef<HTMLInputElement>>('passwordInput');
  private readonly signinMfaOtpInputRef = viewChild<ElementRef<HTMLInputElement>>('signinMfaOtpInput');

  protected logoUrl = computed(() => this.themeService.getLogoUrl());
  protected partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());

  initialEmail = input<string>('');
  nativeAuth = input(false);
  isCiamEnabled = input(false);

  signinMfaChallengeChannel = input<string | undefined>(undefined);
  signinMfaCodeLength = input<number | undefined>(undefined);

  backToLogin = output<void>();
  sendCode = output<NativeAuthSendCodeEvent>();
  resendCode = output<void>();
  verifyCode = output<NativeAuthVerifyCodeEvent>();
  setPassword = output<NativeAuthSetPasswordEvent>();
  continueToSignIn = output<void>();
  signinMfaVerify = output<NativeAuthSigninMfaVerifyEvent>();

  confirmPasswordErrorMatcher = new ConfirmPasswordErrorStateMatcher();

  hidePassword = signal(true);
  hideConfirmPassword = signal(true);

  protected passwordValue = signal('');
  protected passwordHasMinLength = computed(() => this.passwordValue().length >= 8);
  protected passwordHasLetter = computed(() => /[a-zA-Z]/.test(this.passwordValue()));
  protected passwordHasSpecialChar = computed(() => /[^a-zA-Z0-9\s]/.test(this.passwordValue()));
  protected passwordHasNumber = computed(() => /[0-9]/.test(this.passwordValue()));
  loading = signal(false);
  errorMessage = signal<string | null>(null);
  emailError = signal<string | null>(null);
  nativeAuthStep = signal<NativeAuthStep>('send-code');
  codeSent = signal(false);
  emailVerified = signal(false);
  accountCreated = signal(false);
  sendingCode = signal(false);
  verifyingCode = signal(false);

  registerForm = this.fb.group(
    {
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(256), this.passwordStrengthValidator]],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: this.passwordMatchValidator },
  );

  otpControl = new FormControl('', [Validators.required, Validators.minLength(4)]);
  signinMfaOtpControl = new FormControl('', [Validators.required, Validators.minLength(4)]);
  verifyingSigninMfa = signal(false);

  private destroyRef = inject(DestroyRef);

  constructor() {
    this.registerForm
      .get('password')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.passwordValue.set(value ?? ''));

    afterNextRender(() => {
      if (this.isCiamEnabled()) {
        this.firstNameInputRef()?.nativeElement.focus();
      } else {
        this.registerEmailInputRef()?.nativeElement.focus();
      }
    });
  }

  private initialEmailEffect = effect(() => {
    const email = this.initialEmail();
    if (email) {
      this.registerForm.patchValue({ email });
    }
  });

  private nativeAuthFormEffect = effect(() => {
    const verified = this.emailVerified();
    const isNative = this.nativeAuth();
    const sent = this.codeSent();
    const hasInitialEmail = !!this.initialEmail();
    if (!isNative) return;
    const emailControl = this.registerForm.get('email');
    const firstNameControl = this.registerForm.get('firstName');
    const lastNameControl = this.registerForm.get('lastName');
    const passwordControl = this.registerForm.get('password');
    const confirmControl = this.registerForm.get('confirmPassword');
    // Only disable email if it was pre-filled (e.g. from HELOC signup).
    // When arriving via "Sign up now", email should remain editable until code is sent.
    if (hasInitialEmail || sent) {
      emailControl?.disable();
    }
    // Disable name fields after code has been sent so user can't change them mid-flow
    if (sent) {
      firstNameControl?.disable();
      lastNameControl?.disable();
    }
    // Password fields are on page 2 (only rendered after verification).
    // Enable them once verified so Angular doesn't skip their values on submit.
    if (verified) {
      passwordControl?.enable();
      confirmControl?.enable();
    }
  });

  togglePasswordVisibility(): void {
    this.hidePassword.update((hide) => !hide);
  }

  toggleConfirmPasswordVisibility(): void {
    this.hideConfirmPassword.update((hide) => !hide);
  }

  onBackToLogin(): void {
    this.backToLogin.emit();
  }

  advanceToStep(step: NativeAuthStep): void {
    this.nativeAuthStep.set(step);
    this.errorMessage.set(null);
    this.loading.set(false);
    this.sendingCode.set(false);
    this.verifyingCode.set(false);
    this.verifyingSigninMfa.set(false);

    if (step === 'verify-code') {
      this.codeSent.set(true);
    }
    if (step === 'set-password') {
      this.emailVerified.set(true);
    }
    if (step === 'account-created') {
      this.codeSent.set(true);
      this.emailVerified.set(true);
      this.accountCreated.set(true);
    }

    if (step === 'signin-mfa-verify') {
      this.codeSent.set(true);
      this.emailVerified.set(true);
    }

    if (step === 'verify-code' || step === 'set-password' || step === 'signin-mfa-verify') {
      afterNextRender(() => {
        if (step === 'verify-code') {
          this.otpInputRef()?.nativeElement.focus();
        } else if (step === 'set-password') {
          this.passwordInputRef()?.nativeElement.focus();
        } else if (step === 'signin-mfa-verify') {
          this.signinMfaOtpInputRef()?.nativeElement.focus();
        }
      }, { injector: this.injector });
    }
  }

  onSendCode(): void {
    const rawValue = this.registerForm.getRawValue();
    const email = rawValue.email ?? '';
    const firstName = rawValue.firstName ?? '';
    const lastName = rawValue.lastName ?? '';
    if (!firstName || firstName.length < 2) {
      this.registerForm.get('firstName')?.markAsTouched();
      return;
    }
    if (!lastName || lastName.length < 2) {
      this.registerForm.get('lastName')?.markAsTouched();
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.errorMessage.set(this.transloco.translate('commonValidationInvalidEmail'));
      return;
    }
    this.sendingCode.set(true);
    this.errorMessage.set(null);
    this.emailError.set(null);
    this.sendCode.emit({ email, firstName, lastName });
  }

  onResendCode(): void {
    this.sendingCode.set(true);
    this.errorMessage.set(null);
    this.resendCode.emit();
  }

  onVerifyCode(): void {
    if (this.otpControl.invalid) {
      this.otpControl.markAsTouched();
      return;
    }
    this.verifyingCode.set(true);
    this.errorMessage.set(null);
    this.verifyCode.emit({ otp: this.otpControl.value ?? '' });
  }

  onContinueToSignIn(): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.continueToSignIn.emit();
  }

  isCreateAccountStepInvalid(): boolean {
    const email = this.registerForm.get('email');
    const firstName = this.registerForm.get('firstName');
    const lastName = this.registerForm.get('lastName');
    return !email?.value || email?.invalid || !firstName?.value || firstName?.invalid || !lastName?.value || lastName?.invalid;
  }

  isVerifyCodeStepInvalid(): boolean {
    return this.otpControl.invalid;
  }

  isCreatePasswordStepInvalid(): boolean {
    const password = this.registerForm.get('password');
    const confirm = this.registerForm.get('confirmPassword');
    return !!password?.invalid || !!confirm?.invalid || this.registerForm.hasError('passwordMismatch');
  }

  async onSubmit(): Promise<void> {
    if (!this.emailVerified() && !this.codeSent()) {
      this.onSendCode();
      return;
    }
    if (!this.emailVerified() && this.codeSent()) {
      this.onVerifyCode();
      return;
    }
    if (this.nativeAuthStep() === 'signin-mfa-verify') {
      this.onSigninMfaVerify();
      return;
    }
    await this.handleNativeAuthSubmit();
  }

  setLoading(isLoading: boolean): void {
    this.loading.set(isLoading);
  }

  setSendingCode(isSending: boolean): void {
    this.sendingCode.set(isSending);
  }

  setError(message: string | null): void {
    this.errorMessage.set(message);
    this.loading.set(false);
    this.sendingCode.set(false);
    this.verifyingCode.set(false);
    this.verifyingSigninMfa.set(false);
  }

  setEmailError(message: string | null): void {
    this.emailError.set(message);
    this.sendingCode.set(false);
  }

  onSigninMfaVerify(): void {
    if (this.signinMfaOtpControl.invalid) {
      this.signinMfaOtpControl.markAsTouched();
      return;
    }
    this.verifyingSigninMfa.set(true);
    this.errorMessage.set(null);
    this.signinMfaVerify.emit({ otp: this.signinMfaOtpControl.value ?? '' });
  }

  private async handleNativeAuthSubmit(): Promise<void> {
    if (!this.emailVerified()) {
      return;
    }
    const passwordControl = this.registerForm.get('password');
    if (passwordControl?.invalid) {
      passwordControl?.markAsTouched();
      return;
    }
    const passwordVal = passwordControl?.value ?? '';
    const confirmControl = this.registerForm.get('confirmPassword');
    if (confirmControl?.invalid) {
      confirmControl?.markAsTouched();
      return;
    }
    const confirmVal = confirmControl?.value ?? '';
    if (passwordVal !== confirmVal) {
      this.registerForm.setErrors({ passwordMismatch: true });
      return;
    }
    this.loading.set(true);
    this.errorMessage.set(null);
    this.setPassword.emit({ password: passwordVal });
  }

  private passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecialChar = /[^a-zA-Z0-9\s]/.test(value);

    const valid = hasLetter && hasNumber && hasSpecialChar;
    return valid ? null : { passwordStrength: true };
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }
}
